/**
 * Where to go once signing in is done.
 *
 * Router state is not enough for this. It holds only `pathname`, so a query
 * string is dropped; it lives in memory, so a reload during sign-in loses it;
 * and it does not survive an OAuth or OTP round trip at all. A student who
 * followed a chapter link would sign in and land on Home, which is the one
 * place they did not ask to be (CP-2).
 *
 * `sessionStorage`, so it dies with the tab and is never a cookie. Only
 * in-app paths are stored: a destination is somewhere in this app, and an
 * absolute URL here would be an open redirect.
 */

const KEY = 'return_to';

/** Store a full destination — path AND query. */
export function rememberReturnTo(destination: string): void {
  if (!destination.startsWith('/') || destination.startsWith('//')) return;
  try { sessionStorage.setItem(KEY, destination); } catch { /* private mode: the default applies */ }
}

/** Take the stored destination, clearing it. `fallback` when there is none. */
export function takeReturnTo(fallback = '/home'): string {
  try {
    const stored = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (stored && stored.startsWith('/') && !stored.startsWith('//')) return stored;
  } catch { /* private mode: fall through */ }
  return fallback;
}

/** Read it without consuming it — for a screen that has to decide whether it is
 *  in the middle of somebody's arrival before it knows if it will finish. */
export function peekReturnTo(): string | null {
  try {
    const stored = sessionStorage.getItem(KEY);
    return stored && stored.startsWith('/') && !stored.startsWith('//') ? stored : null;
  } catch {
    return null;
  }
}
